import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TrendingUp, TrendingDown, Minus, CheckCircle2, Activity, BarChart3 } from "lucide-react";
import { PageHead } from "@/components/AppLayout";
import { Card, CardHead, Badge, Avatar, EmptyState, statusTone } from "@/components/ui-kit";
import api from "@/lib/api";

export default function Outcomes() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/outcomes").then((r) => setData(r.data)); }, []);
  if (!data) return null;
  const { orders, summary } = data;

  const KPIS = [
    { label: "Ordered Cases", value: summary.total_orders, icon: Activity, tone: "bg-sky-50 text-sky-600" },
    { label: "In Treatment", value: summary.in_treatment, icon: BarChart3, tone: "bg-teal-50 text-teal-600" },
    { label: "Completed", value: summary.completed, icon: CheckCircle2, tone: "bg-emerald-50 text-emerald-600" },
  ];

  return (
    <>
      <PageHead title="Outcomes & Progress" subtitle="Recovery progress on the treatment orders you've placed." />
      <div className="grid grid-cols-3 gap-4 mb-5">
        {KPIS.map((s) => (
          <Card key={s.label} hover className="p-5 flex gap-3.5 items-start" data-testid={`outcome-kpi-${s.label.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${s.tone}`}><s.icon size={20} /></div>
            <div><div className="metric text-2xl font-extrabold text-slate-900 leading-none">{s.value}</div><div className="text-[12.5px] text-slate-500 mt-1">{s.label}</div></div>
          </Card>
        ))}
      </div>
      <Card>
        <CardHead title="Ordered Cases — Progress" />
        {orders.length === 0 ? (
          <EmptyState icon={<TrendingUp size={24} />} title="No outcomes yet"
            subtitle="Once you submit treatment orders, their recovery progress will appear here." />
        ) : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead><tr>{["Patient", "Diagnosis", "Physiotherapist", "Progress", "Pain", "Status"].map((h) => (
              <th key={h} className="pq-label px-4 py-3 text-left border-b border-slate-100">{h}</th>))}</tr></thead>
            <tbody>
              {orders.map((o) => {
                const oc = o.outcome || {};
                const pt = oc.pain_trend || [];
                const latestPain = pt.length ? pt[pt.length - 1] : null;
                const prevPain = pt.length > 1 ? pt[pt.length - 2] : null;
                return (
                  <tr key={o.id} onClick={() => nav(`/referrals/${o.id}`)} data-testid={`outcome-row-${o.id}`}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer">
                    <td className="px-4 py-3"><div className="flex items-center gap-2.5"><Avatar name={o.patient_name} size={32} /><div className="font-semibold text-[13px]">{o.patient_name}</div></div></td>
                    <td className="px-4 py-3 text-[13px]">{o.diagnosis_label}</td>
                    <td className="px-4 py-3 text-[13px]">{o.assigned_physio?.name || <span className="text-amber-600">Matching…</span>}</td>
                    <td className="px-4 py-3"><div className="flex items-center gap-2"><div className="w-16 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-teal-600" style={{ width: `${oc.progress || 0}%` }} /></div><span className="text-[12px]">{oc.progress || 0}%</span></div></td>
                    <td className="px-4 py-3" data-testid={`outcome-pain-${o.id}`}>
                      {latestPain == null ? <span className="text-slate-300 text-[12px]">—</span> : (
                        <span className="inline-flex items-center gap-1.5">
                          <span className={`font-bold text-[13px] ${latestPain >= 7 ? "text-rose-600" : latestPain >= 4 ? "text-amber-600" : "text-emerald-600"}`}>{latestPain}/10</span>
                          {prevPain != null && latestPain < prevPain && <TrendingDown size={14} className="text-emerald-600" />}
                          {prevPain != null && latestPain > prevPain && <TrendingUp size={14} className="text-rose-500" />}
                          {prevPain != null && latestPain === prevPain && <Minus size={14} className="text-slate-400" />}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3"><Badge tone={statusTone(o.status)}>{oc.status || o.status}</Badge></td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
      </Card>
    </>
  );
}
